import styled from 'styled-components'
import { useParams } from 'react-router'

import useQuery from '../../hooks/useQuery'

import { useIsOffline } from '@oneblink/apps-react'

import {
  DownloadPdf,
  InfoPanel,
  InfoPanelGrey,
  TrackingCodePanel,
  SendPdfEmail,
} from './ReceiptComponents'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`
Container.displayName = 'Container'

function ReceiptBodyContent() {
  const { vetDecPending, isInPendingQueue } = useQuery()
  const vetDecPendingBool = vetDecPending === 'true'
  const submittedOffline = isInPendingQueue === 'true'

  const isOffline = useIsOffline()

  if (!isOffline && !submittedOffline && !vetDecPendingBool) {
    return (
      <InfoPanelGrey className="fa-info-panel fa-info-panel__grey">
        Your Record of Movement form has been successfully submitted. A copy of
        this form has been sent to your email address with a printable summary
        report. A digital or hard copy of this form must accompany the animals
        to their destination and be presented to an authorised officer upon
        request.
      </InfoPanelGrey>
    )
  }

  if ((isOffline || submittedOffline) && !vetDecPendingBool) {
    return (
      <InfoPanelGrey className="fa-info-panel fa-info-panel__grey">
        Your Record of Movement form has been saved and is now queued in the
        ‘Pending’ folder. When your device has mobile coverage or is connected
        to Wi-Fi, your form will be submitted. A copy of this form will then be
        sent to your email address with a printable summary report which
        contains your reference code.
      </InfoPanelGrey>
    )
  }

  if (!isOffline && !submittedOffline && vetDecPendingBool) {
    return (
      <InfoPanelGrey className="fa-info-panel fa-info-panel__grey">
        <span>
          Your Record of Movement form has been successfully submitted. This
          form will now be sent to the registered veterinarian’s email address
          for completion of the veterinarian declaration. You will be notified
          by email when the veterinarian declaration has been completed. Please
          follow up with your veterinarian if you have not received the
          finalised form. A completed digital or hard copy of this form must
          accompany the animals to their destination and be presented to an
          authorised officer upon request.
        </span>
      </InfoPanelGrey>
    )
  }

  if ((isOffline || submittedOffline) && vetDecPendingBool) {
    return (
      <InfoPanelGrey className="fa-info-panel fa-info-panel__grey">
        <span>
          Your Record of Movement form has been saved and is now queued in the
          ‘Pending’ folder. When your device has mobile coverage or is connected
          to Wi-Fi, the form will be sent to the registered veterinarian’s email
          address for completion of the veterinarian declaration. You will be
          notified by email when the veterinarian declaration has been
          completed. Please follow up with your veterinarian if you have not
          received the finalised form.
        </span>
      </InfoPanelGrey>
    )
  }

  return null
}

function ReceiptFooterContent() {
  const { vetDecPending, enteringNsw } = useQuery()
  const vetDecPendingBool = vetDecPending === 'true'
  const enteringNswBool = enteringNsw === 'true'

  if (vetDecPendingBool || enteringNswBool) {
    return (
      <InfoPanel className="fa-info-panel">
        <span>
          For any queries please contact 02 6626 1201, or email{' '}
          <a href="mailto:cattletick@dpi.nsw.gov.au">
            cattletick@dpi.nsw.gov.au
          </a>{' '}
          and quote your reference code.
        </span>
      </InfoPanel>
    )
  }

  if (!enteringNswBool) {
    return (
      <InfoPanel className="fa-info-panel">
        <span>
          For any queries please contact 02 6632 6901 or email{' '}
          <a href="mailto:kyoglecattletick@dpi.nsw.gov.au">
            kyoglecattletick@dpi.nsw.gov.au
          </a>{' '}
          and quote your reference code.
        </span>
      </InfoPanel>
    )
  }

  return null
}

export default function Receipt() {
  const { trackingCode, submissionId, vetDecPending } = useQuery()
  const vetDecPendingBool = vetDecPending === 'true'

  const isOffline = useIsOffline()
  const { formId: formIdStr } = useParams<{ formId: string }>()
  const formId = parseInt(formIdStr)

  return (
    <Container className="fa-form-receipt">
      {!isOffline && trackingCode && (
        <TrackingCodePanel trackingCode={trackingCode as string} />
      )}
      <ReceiptBodyContent />
      {!vetDecPendingBool && !isOffline && trackingCode && (
        <>
          <DownloadPdf
            trackingCode={trackingCode as string}
            submissionId={submissionId as string}
            formId={formId}
          />
          <SendPdfEmail submissionId={submissionId as string} formId={formId} />
        </>
      )}
      <ReceiptFooterContent />
    </Container>
  )
}
